export interface ReleaseNote {
  date: string;
  kind: 'feature' | 'fix';
  title: string;
  description: string;
}

// Hand-maintained — unlike the model-availability changelog (computed by
// diffing each refresh), this describes changes to the app itself, so
// there's nothing to compute it from. Add an entry here alongside any
// change worth telling users about. Newest first.
export const RELEASE_NOTES: ReleaseNote[] = [
  {
    date: '2026-08-15',
    kind: 'feature',
    title: "What's New and the RSS feed now show deployment tier",
    description: 'Model availability updates previously just said a model was new, removed, or now available in a region, with no way to tell if that meant On-demand/Standard or Provisioned capacity. New, removed, and region-expansion entries in the What’s New panel and the RSS feed now name the deployment tier alongside each region, e.g. "us-east-1 (On-demand), eu-west-2 (Provisioned)". Entries published before this change are unaffected and will age out of the (capped) list as usual.',
  },
  {
    date: '2026-08-03',
    kind: 'feature',
    title: 'Standard vs Provisioned deployment tier now shown',
    description: 'In-region listings previously all looked the same regardless of deployment type. Native regions now show whether a model is On-demand/Standard (pay-as-you-go) or Provisioned (reserved capacity) — in the location view, the model drawer, and the model table — for both AWS and Azure.',
  },
  {
    date: '2026-07-26',
    kind: 'feature',
    title: 'Manual light/dark theme toggle',
    description: 'The site previously only followed your OS light/dark setting with no way to override it. A new Auto/Light/Dark switch in the header lets you force either theme regardless of your system setting, and remembers your choice.',
  },
  {
    date: '2026-07-26',
    kind: 'feature',
    title: '"/" now jumps to search',
    description: 'Press "/" anywhere on the page to jump straight to the model search box, the same shortcut GitHub uses — a small "/" hint now shows inside the empty search box itself so it’s discoverable without needing to be told. It won’t interfere with typing "/" as part of a search term or in any other text field.',
  },
  {
    date: '2026-07-26',
    kind: 'feature',
    title: 'More accessibility fixes: filters and the location view',
    description: 'Following on from the table/drawer accessibility pass: the search box and provider/modality filters now have proper labels for screen readers, the result count announces itself as you filter, and each model card in the location view now has a distinct "Global availability" button instead of dozens of identically-named ones.',
  },
  {
    date: '2026-07-26',
    kind: 'fix',
    title: 'Visitor count now actually shows up',
    description: 'The visitor-count stat added yesterday never appeared — the daily job crashed on every run because of a permissions gap that stopped it from telling "the file doesn’t exist yet" apart from "access denied". The Traffic column in the footer now populates as intended.',
  },
  {
    date: '2026-07-26',
    kind: 'feature',
    title: 'Model table and details drawer are now keyboard-accessible',
    description: 'Opening a model’s details previously required a mouse click, with no way to reach it via Tab/Enter. Rows and mobile cards are now focusable and operable from the keyboard, and the details drawer traps focus while open and returns it to whatever you opened it from when you close it.',
  },
  {
    date: '2026-07-25',
    kind: 'feature',
    title: "What's New now catches legacy status flips",
    description: 'Previously, a model quietly going legacy (or coming back) in a region it was already listed in produced no changelog entry — only brand-new/removed models and region expansions were tracked. The daily diff now also compares each region’s lifecycle status between refreshes and reports "now legacy in …" / "active again in …" entries.',
  },
  {
    date: '2026-07-25',
    kind: 'feature',
    title: 'Search now tolerates typos',
    description: 'The model search box falls back to a fuzzy match when there’s no exact hit, so a typo like "clade" still finds "claude".',
  },
  {
    date: '2026-07-25',
    kind: 'fix',
    title: 'Fixed inaccurate "data may leave" warning',
    description: 'Cross-region routing within a single country (e.g. a us.* profile hopping between US regions) was flagged as "data may leave United States" even though it never does. The warning now checks the actual routing destinations and only appears when data can genuinely leave the selected location.',
  },
  {
    date: '2026-07-25',
    kind: 'feature',
    title: 'Page content centered on wide screens',
    description: 'Main content and the footer now cap at a max width and center on wide monitors instead of hugging the left edge; the footer columns are also reordered — About and Traffic on the left, Links on the right.',
  },
  {
    date: '2026-07-25',
    kind: 'feature',
    title: 'Footer reorganized into columns',
    description: 'The footer was one long centered paragraph; it’s now split into About, Links, and Traffic columns so each is easier to scan, and still stacks vertically on narrow screens.',
  },
  {
    date: '2026-07-25',
    kind: 'feature',
    title: 'Visitor count added to the footer',
    description: 'A new daily job counts unique visitors from CloudFront’s own access logs — no client-side tracking script or third-party analytics service — and shows the total in the footer alongside a 7-day rolling count.',
  },
  {
    date: '2026-07-25',
    kind: 'feature',
    title: 'Dark mode support',
    description: 'The site now follows your OS light/dark preference automatically — no manual toggle needed.',
  },
  {
    date: '2026-07-20',
    kind: 'fix',
    title: "Active/Legacy status now reflects the exact region you're viewing",
    description: 'A model could previously show "Active" even though it was actually deprecated in the specific region you called. Status is now tracked per region, and shows "Mixed" when regions disagree instead of guessing.',
  },
  {
    date: '2026-07-20',
    kind: 'feature',
    title: 'Pick a whole country instead of one region',
    description: '"United Kingdom — all providers" (for example) now appears in the region picker, showing every AWS and Azure model available anywhere in that country in one view — no need to check eu-west-2 and uksouth separately.',
  },
  {
    date: '2026-07-20',
    kind: 'feature',
    title: 'Azure regions now grouped by country and continent',
    description: 'Azure regions used to fall into an unlabeled "Other" group and only ever offered "this region only" residency. They now group correctly (e.g. under Europe) and get the same country/continent residency options AWS regions already had.',
  },
  {
    date: '2026-07-20',
    kind: 'feature',
    title: 'Azure OpenAI and Microsoft models added',
    description: 'Azure models now show up alongside AWS Bedrock listings, each tagged with a cloud badge, and grouped together with same-publisher models from either cloud.',
  },
];
